import EventEmitter from "../util/event-emitter";

let nextID = 0;

export default class DataObject extends EventEmitter {
    /**
     * @param  {Object} data A javascript object of key, value pairs that represents the data this object is to manage
     * @param  {Object} options Configuration for this DataObject
     */
    constructor(data = undefined, options = undefined) {
        super(options);
        this.options = options || {};

        // apply options
        Object.assign(this, this.options);

        if (!this.id) {
            this.id = (nextID++).toString();
        }
        if (data) {
            this.setData(data);
        }
    }

    /**
     * The id of this DataObject, generated automatically or passed in options
     *
     * @type {String}
     */
    id = null;

    /**
     * Reference to the store that this component is attached to Store
     * @type {Store}
     */
    store = null;

    /**
     * The raw Object data for this DataObject
     *
     * @type {Object}
     * @private
     */
    raw = {};

    /**
     * Internal property that keeps an index of all modified values
     * @type {Object}
     */
    modified = {};

    /**
     * Internal property for storing the data in this DataObject
     * @type {Object}
     */
    data = {};

    /**
     * Internal flag for is this DataObject has been modified
     * @type {Boolean}
     */
    dirty = false;

    /**
     * Configuration option for this DataObject to write it's modified data automatically
     * @type {Boolean}
     */
    autoWrite = false;

    /**
     * Setter for updating a property, recommend to use this as opposed to modifying data directly
     * as it will track the dirty state and trigger events
     * @fires modelchanged
     *
     * @param name {String}
     * @param value {Object}
     */
    set(name, value) {
        this.modified[name] = value;
        this.dirty = true;
        /**
         * @event modelchanged Event triggers whenever that data within this object has been modified
         * @param {DataObject} record The object that has changed
         * @param {String} field The field name that has changed
         * @param {String/Number/Object} value The value that is now set on the field
         *
         */
        this.trigger("modelchanged", [this, name, value]);
        if (this.autoWrite) {
            this.write();
        }
    }

    /**
     * Returns the data saved for a given key. Dot notation can be used to access nested objects.
     * This also returns unsaved dirty data, so may not necessarily reflect the state of data in `this.data`.
     *
     * @param  {String} objectPath Name of the data attribute. Can be dot-notated for nested objects
     * @return {Object}
     */
    get(objectPath) {
        const components = objectPath.split(".");
        const name = components.shift();
        // attrs remain after shift
        const attrs = components;
        let dataObj = this.modified[name] || this.data[name];

        attrs.forEach((i) => {
            if (dataObj) {
                dataObj = dataObj[i];
            }
        });
        return dataObj;
    }

    /**
     * @param {Object} data
     * @param {Boolean} [dirty] Trigger a dirty write
     * @param {Boolean} [update] Trigger an update event
     */
    setData(data, dirty, update) {
        this.raw = data;
        if (!update) {
            this.data = {};
            this.modified = {};
        }
        Object.entries(data).forEach(([key, value]) => {
            if (dirty) {
                this.set(key, value);
            } else {
                this.data[key] = value;
            }
        });
    }

    /**
     * Write the modified data to the main record
     */
    write() {
        Object.entries(this.modified).forEach(([key, value]) => {
            this.data[key] = value;
        });
        this.dirty = false;
        this.modified = {};
        /**
         * @event modelwrite
         * Fires when the changes have been saved from the modified buffer
         * @param {DataObject} cmp A reference to the component that triggered the event
         */
        this.trigger("modelwrite", [this]);
    }
}
